import { StatsBreakdownGroup } from '@numistic/contracts'
import { useStatsBreakdown, useStatsSummary } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { createRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'

import { useErrorText } from '../lib/errors.js'
import { requireSession } from '../lib/guards.js'
import { formatMoney } from '../lib/format.js'
import { rootRoute } from './root.js'

export const statsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats',
  validateSearch: z.object({ group: StatsBreakdownGroup.optional().catch(undefined) }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: StatsPage,
})

function StatsPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { group = 'issuer' } = statsRoute.useSearch()
  const summary = useStatsSummary()
  const breakdown = useStatsBreakdown(group)
  const s = summary.data
  if (summary.error) return <Alert variant="destructive">{errorText.request(summary.error)}</Alert>
  if (!s) return null
  const money = (v: string) => formatMoney(v, s.baseCurrency)
  const maxSpent = Math.max(1, ...(breakdown.data ?? []).map((r) => Number(r.spent)))

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <h1 className="text-2xl font-semibold">{t('stats.pageTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('stats.baseCurrency', { currency: s.baseCurrency })}{' '}
          <Link
            to="/settings/profile"
            className="text-primary underline underline-offset-4 hover:decoration-2"
          >
            {t('stats.changeCurrency')}
          </Link>
        </p>
      </div>
      {s.unconverted ? <Alert>{t('stats.unconverted', { count: s.unconverted })}</Alert> : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <Metric title={t('stats.spent')} value={money(s.spent)} hint={t('stats.spentHint')} />
        <Metric
          title={t('stats.value')}
          value={money(s.value)}
          hint={t('stats.valueHint', { valued: s.valued, owned: s.owned })}
        />
        <Metric
          title={t('stats.profit')}
          value={money(s.soldProfit)}
          hint={t('stats.profitHint', { revenue: money(s.soldRevenue), cost: money(s.soldCost) })}
          negative={Number(s.soldProfit) < 0}
        />
      </div>

      <section className="grid gap-3">
        <nav className="flex flex-wrap gap-2" aria-label={t('stats.pageTitle')}>
          {StatsBreakdownGroup.options.map((g) => (
            <Link
              key={g}
              to="/stats"
              search={{ group: g }}
              aria-current={g === group ? 'page' : undefined}
              className="rounded-full border px-3 py-1 text-sm aria-[current=page]:border-primary aria-[current=page]:bg-primary/10"
            >
              {t(`stats.groups.${g}`)}
            </Link>
          ))}
        </nav>
        {breakdown.error ? (
          <Alert variant="destructive">{errorText.request(breakdown.error)}</Alert>
        ) : null}
        {breakdown.data?.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('stats.empty')}</p>
        ) : null}
        {breakdown.data?.length ? (
          <Table>
            <THead>
              <Tr>
                <Th>{t('stats.columns.label')}</Th>
                <Th className="text-right">{t('stats.columns.count')}</Th>
                <Th className="text-right">{t('stats.columns.spent')}</Th>
                <Th className="text-right">{t('stats.columns.value')}</Th>
              </Tr>
            </THead>
            <TBody>
              {breakdown.data.map((r) => (
                <Tr key={r.key ?? 'none'}>
                  <Td>
                    <div className="grid gap-1">
                      <span>{r.label}</span>
                      <span
                        className="h-1 rounded bg-primary/60"
                        style={{ width: `${(Number(r.spent) / maxSpent) * 100}%` }}
                        aria-hidden
                      />
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">{r.count}</Td>
                  <Td className="text-right tabular-nums">{money(r.spent)}</Td>
                  <Td className="text-right tabular-nums">{money(r.value)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : null}
      </section>
      <p className="text-xs text-muted-foreground">
        {t('stats.specimens', { count: s.specimens })}
      </p>
    </div>
  )
}

function Metric({
  title,
  value,
  hint,
  negative,
}: {
  title: string
  value: string
  hint: string
  negative?: boolean
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-1">
        <span className={cn('text-2xl font-semibold tabular-nums', negative && 'text-destructive')}>
          {value}
        </span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </CardContent>
    </Card>
  )
}
